import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import InvoiceTable from "@/components/InvoiceTable";
import ManageBillingButton from "@/components/ManageBillingButton";
import { Info, Search, Layers } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "paid", label: "Paid" },
  { key: "overdue", label: "Overdue" },
];

export default function Invoices() {
  const { param, allMode } = useDataScope();
  const [invoices, setInvoices] = useState([]);
  const [demo, setDemo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (allMode) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/invoices${param ? `?${param}` : ""}`);
        if (cancelled) return;
        setInvoices(data.invoices || []);
        setDemo(data.uses_demo_data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [param, allMode]);

  const filtered = useMemo(() => {
    let list = invoices;
    if (filter !== "all") list = list.filter((i) => i.status === filter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((i) => (i.number || "").toLowerCase().includes(q) || (i.description || "").toLowerCase().includes(q));
    }
    return list;
  }, [invoices, filter, query]);

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Invoices</h1>
          <p className="text-slate-500 mt-1">All your invoices in one place.</p>
        </div>
        {!allMode && <ManageBillingButton accountParam={param} />}
      </div>

      {allMode ? (
        <div data-testid="invoices-all-mode-prompt" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-5 py-6">
          <Layers className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>Select a specific store to view invoices &amp; billing.</strong>
            <p className="mt-1 text-blue-700">Billing differs per store, so invoices and the billing portal aren't aggregated in the "All accounts" view. Pick a client from the account switcher (top-right) to see their invoices.</p>
          </div>
        </div>
      ) : (
        <>
          {demo && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg px-4 py-3">
              <Info className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                <strong>Demo data shown.</strong> Your live invoices will appear here once your billing account is connected.
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-2" data-testid="invoice-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${
                      filter === f.key
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="invoice-search-input"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search invoices…"
                  className="pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 w-full md:w-72"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-slate-500 py-10 text-center">Loading invoices…</div>
            ) : (
              <InvoiceTable invoices={filtered} testId="invoices-table" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
